/**
 * Full-page notification feed. Shared by the vendor console and the pet-owner app
 * (the /user/notifications endpoint resolves the recipient from the token). The
 * booking deep-link target is derived from the session role.
 */
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Loader2 } from 'lucide-react';
import { SectionHeader } from '@/components/ui/section-header';
import { Button } from '@/components/ui/button';
import { use_all_notifications, use_unread_count, use_notification_actions } from '@/features/notifications/hooks';
import { use_session_store } from '@/lib/session-store';
import type { NotificationItem } from '@/features/notifications/types';

function fmt(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const role = use_session_store((s) => s.role);
  const booking_path = role === 'vendor' ? '/vendor/app/bookings' : '/app/bookings';

  const list_q = use_all_notifications();
  const count_q = use_unread_count();
  const { mark_read, mark_all_read } = use_notification_actions();

  const items = list_q.data ?? [];
  const count = count_q.data ?? 0;

  function on_item(n: NotificationItem) {
    if (!n.read_at) mark_read.mutate(n.id);
    if (n.booking_id) navigate(booking_path);
  }

  return (
    <div className="flex flex-col gap-5" data-testid="notifications-page">
      <div className="flex items-center justify-between gap-3">
        <SectionHeader title="Notifications" description="Everything that's happened on your account." />
        {count > 0 && (
          <Button size="sm" variant="ghost" onClick={() => mark_all_read.mutate()} data-testid="notifications-mark-all">
            <Check className="h-4 w-4" /> Mark all read
          </Button>
        )}
      </div>

      {list_q.isLoading ? (
        <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-card border border-border p-10 text-center text-sm text-muted-foreground" data-testid="notifications-empty">
          <Bell className="h-6 w-6" />
          You're all caught up.
        </div>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-card border border-border" data-testid="notifications-list">
          {items.map((n) => (
            <li key={n.id}>
              <button
                type="button"
                onClick={() => on_item(n)}
                className={`flex w-full flex-col gap-0.5 px-4 py-3 text-left transition-colors hover:bg-muted/50 ${n.read_at ? '' : 'bg-primary/5'}`}
                data-testid={`notifications-item-${n.id}`}
              >
                <div className="flex items-center gap-2">
                  {!n.read_at && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
                  <span className="text-sm font-semibold">{n.title}</span>
                  <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">{fmt(n.created_at)}</span>
                </div>
                <span className="text-xs text-muted-foreground">{n.body}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
